import { useAbortController } from '@packages/core/http';
import {
    type FailCodeConcernEntity,
    type FailCodeFrequencyEntity,
    type FailCodeLocationEntity,
    FetchConcerns,
    FetchFrequencies,
    FetchLocations,
} from '@packages/domain/fail-codes';
import { container } from '@web/services/container';
import { useCallback, useEffect, useState } from 'react';

import type { FailCodeSelection } from '../fail-codes-modal.types';

/**
 * A typeahead option with a display label and a code value.
 * Used locally for the react-bootstrap-typeahead dropdowns in the NTF cascade.
 */
type TypeaheadOption = {
    /** Human-readable label combining code and description (e.g. "A - Engine"). */
    label: string;

    /** The raw code value sent to the API and used in the final selection. */
    value: string;
};

// Operations are prefixed with $ to make them visually distinct in this file.
const [$fetchFailCodeConcerns, $fetchFailCodeFrequencies, $fetchFailCodeLocations] = container.resolveOperations(
    FetchConcerns,
    FetchFrequencies,
    FetchLocations
);

/**
 * Manages the 3-level cascading selection for the No Trouble Found fail code search method.
 * The cascade flows: location -> frequency -> concern. Each level's selection clears all
 * downstream levels and fetches options for the next level in the chain.
 *
 * This hook owns all state, API calls, and cascade logic so the component can remain a
 * presentational shell.
 *
 * @returns hook state and handlers for the No Trouble Found cascade
 */
export function useNoTroubleFound() {
    // State
    const [concernOptionData, setConcernOptionData] = useState<TypeaheadOption[]>([]);
    const [error, setError] = useState('');
    const [frequencyOptionData, setFrequencyOptionData] = useState<TypeaheadOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [locationOptionData, setLocationOptionData] = useState<TypeaheadOption[]>([]);
    const [selectedConcern, setSelectedConcern] = useState<TypeaheadOption[]>([]);
    const [selectedFrequency, setSelectedFrequency] = useState<TypeaheadOption[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<TypeaheadOption[]>([]);

    // Hooks
    const { abortSignalRef } = useAbortController();

    // Derived state
    const canConfirm =
        Boolean(selectedLocation.length) && Boolean(selectedFrequency.length) && Boolean(selectedConcern.length);

    // Handlers

    /**
     * Fetch all available locations on mount. Locations are the first level of the
     * NTF cascade and do not depend on any prior selection.
     */
    const fetchLocations = useCallback(async () => {
        setIsLoading(true);

        try {
            const locations = await $fetchFailCodeLocations({ signal: abortSignalRef.current });

            // Map domain entities to typeahead options
            const options = locations.map(
                (location: FailCodeLocationEntity): TypeaheadOption => ({
                    label: `${location.code} - ${location.description}`,
                    value: location.code,
                })
            );

            setLocationOptionData(options);
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to fetch locations');
        } finally {
            setIsLoading(false);
        }
    }, [abortSignalRef]);

    /**
     * Fetch frequencies for a selected location code. Frequencies are the second level
     * of the NTF cascade.
     *
     * @param locationCode - the code of the selected location
     */
    const fetchFrequencies = useCallback(
        async (locationCode: string) => {
            try {
                const frequencies = await $fetchFailCodeFrequencies({
                    locationCode,
                    signal: abortSignalRef.current,
                });

                // Map domain entities to typeahead options
                const options = frequencies.map(
                    (frequency: FailCodeFrequencyEntity): TypeaheadOption => ({
                        label: `${frequency.code} - ${frequency.description}`,
                        value: frequency.code,
                    })
                );

                setFrequencyOptionData(options);
            } catch (error) {
                setError(error instanceof Error ? error.message : 'Failed to fetch frequencies');
            }
        },
        [abortSignalRef]
    );

    /**
     * Fetch concerns for a selected location and frequency code combination. Concerns
     * are the third and final level of the NTF cascade.
     *
     * @param locationCode - the code of the selected location
     * @param frequencyCode - the code of the selected frequency
     */
    const fetchConcerns = useCallback(
        async (locationCode: string, frequencyCode: string) => {
            try {
                const concerns = await $fetchFailCodeConcerns({
                    frequencyCode,
                    locationCode,
                    signal: abortSignalRef.current,
                });

                // Map domain entities to typeahead options
                const options = concerns.map(
                    (concern: FailCodeConcernEntity): TypeaheadOption => ({
                        label: `${concern.code} - ${concern.description}`,
                        value: concern.code,
                    })
                );

                setConcernOptionData(options);
            } catch (error) {
                setError(error instanceof Error ? error.message : 'Failed to fetch concerns');
            }
        },
        [abortSignalRef]
    );

    /**
     * Handle the user selecting a location. Clears downstream selections (frequency and
     * concern) and fetches frequency options for the selected location.
     *
     * @param selected - the typeahead selection array (single-select, so 0 or 1 element)
     */
    const handleLocationChange = useCallback(
        (selected: TypeaheadOption[]) => {
            setError('');
            setSelectedLocation(selected);

            // Clear downstream selections and options
            setSelectedFrequency([]);
            setFrequencyOptionData([]);
            setSelectedConcern([]);
            setConcernOptionData([]);

            const locationCode = selected[0]?.value;

            if (locationCode) {
                fetchFrequencies(locationCode);
            }
        },
        [fetchFrequencies]
    );

    /**
     * Handle the user selecting a frequency. Clears the concern selection and fetches
     * concern options for the selected location + frequency combination.
     *
     * @param selected - the typeahead selection array (single-select, so 0 or 1 element)
     */
    const handleFrequencyChange = useCallback(
        (selected: TypeaheadOption[]) => {
            setError('');
            setSelectedFrequency(selected);

            // Clear downstream selection and options
            setSelectedConcern([]);
            setConcernOptionData([]);

            const locationCode = selectedLocation[0]?.value;
            const frequencyCode = selected[0]?.value;

            if (locationCode && frequencyCode) {
                fetchConcerns(locationCode, frequencyCode);
            }
        },
        [fetchConcerns, selectedLocation]
    );

    /**
     * Handle the user selecting a concern. This is the final level of the cascade;
     * no downstream fetches are triggered.
     *
     * @param selected - the typeahead selection array (single-select, so 0 or 1 element)
     */
    const handleConcernChange = useCallback((selected: TypeaheadOption[]) => {
        setError('');
        setSelectedConcern(selected);
    }, []);

    /**
     * Build and return the final fail code selection from the current cascade state.
     * For no-trouble-found, the location code serves as both failCode and repairCode,
     * the frequency code maps to section, and the concern code maps to subSection.
     *
     * @returns the fail code selection object ready for the parent callback
     */
    function confirmSelection(): FailCodeSelection {
        return {
            failCode: selectedLocation[0].value,
            repairCode: selectedLocation[0].value,
            section: selectedFrequency[0].value,
            subSection: selectedConcern[0].value,
        };
    }

    // Effects

    /** Fetch locations on mount. */
    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    return {
        /** Whether all three cascade levels have a selection and the user can confirm. */
        canConfirm,

        /** Available concern options for the third-level typeahead. */
        concernOptionData,

        /**
         * Build the final `FailCodeSelection` from the current cascade state.
         * Only valid when `canConfirm` is `true`.
         */
        confirmSelection,

        /** Error message from the most recent failed API call, or empty string. */
        error,

        /** Available frequency options for the second-level typeahead. */
        frequencyOptionData,

        /** Handle the user selecting a concern (third level). */
        handleConcernChange,

        /** Handle the user selecting a frequency (second level). */
        handleFrequencyChange,

        /** Handle the user selecting a location (first level). */
        handleLocationChange,

        /** Whether locations are currently being fetched (initial load). */
        isLoading,

        /** Available location options for the first-level typeahead. */
        locationOptionData,

        /** The currently selected concern (array with 0 or 1 element). */
        selectedConcern,

        /** The currently selected frequency (array with 0 or 1 element). */
        selectedFrequency,

        /** The currently selected location (array with 0 or 1 element). */
        selectedLocation,
    };
}
